import { requireClient, supabase, isConfigured } from './supabase.js';
import { currentUser } from './auth.js';

/* Blobs live in Storage; documents carry ids.

   This is the whole reason the cloud version needs a doc v2. In v1 a photo, a
   texture and a model are each a base64 data URL sitting inside the document, so
   a project with one 20 MB model is a ~27 MB jsonb row that has to be sent in
   full on every autosave. Storing them apart makes the document small enough
   that saving on every history step is unremarkable.

   The single-file build already worked around this locally with srcVault, which
   kept data URLs out of the undo stack. This is the same split, one layer down. */

const BUCKET = 'assets';

/* ── signed URLs ─────────────────────────────────────────────────────────────
   The bucket is private, so every read goes through a signed URL. Eight hours is
   chosen against the failure it prevents: textures and models are decoded once
   into texCache or geometry and never re-fetched, so expiry cannot touch them —
   but the reference photo stays live in four <img> elements for as long as the
   tab is open, and a session outliving the signature is a silently blank photo.
   Re-signing when the tab regains focus covers the long lunch break. */
const SIGN_SECONDS = 8 * 60 * 60;

const urlCache = new Map();      // assetId -> { url, expires }
let signedAt = 0;

/** Sign many at once — one request for a whole project rather than one per asset. */
export async function signedUrls(assetIds) {
  const ids = [...new Set(assetIds.filter(Boolean))];
  if (!ids.length || !isConfigured) return new Map();

  const fresh = new Map();
  const need = [];
  const now = Date.now();
  for (const id of ids) {
    const hit = urlCache.get(id);
    if (hit && hit.expires > now + 60_000) fresh.set(id, hit.url);
    else need.push(id);
  }
  if (!need.length) return fresh;

  const client = requireClient();
  const { data: rows, error } = await client
    .from('assets').select('id, storage_path').in('id', need);
  if (error) throw error;

  const { data, error: sErr } = await client
    .storage.from(BUCKET)
    .createSignedUrls(rows.map(r => r.storage_path), SIGN_SECONDS);
  if (sErr) throw sErr;

  const expires = now + SIGN_SECONDS * 1000;
  data.forEach((d, i) => {
    if (d.error || !d.signedUrl) return;
    const id = rows[i].id;
    urlCache.set(id, { url: d.signedUrl, expires });
    fresh.set(id, d.signedUrl);
  });
  signedAt = now;
  return fresh;
}

export const signedUrl = async id => (await signedUrls([id])).get(id) || null;

/** Drop the cache so the next read re-signs. */
export function invalidateUrls() { urlCache.clear(); }

/* Re-sign on return to the tab. Only the live <img> elements can go stale, so
   the listener is registered by whoever owns them; this just clears the cache. */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - signedAt > SIGN_SECONDS * 1000 * 0.5) invalidateUrls();
  });
}

/* ── upload ──────────────────────────────────────────────────────────────── */

/** sha-256, hex. Used to notice a file that has already been uploaded. */
export async function checksum(blob) {
  const buf = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(hash)].map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Upload a Blob and record it. Returns the asset row.
 *
 * A repeat upload of the same bytes returns the existing row instead of storing
 * a second copy — the same reference photo used in three projects is one object,
 * and it is the checksum index that makes that lookup cheap.
 *
 * @param {Blob}   blob
 * @param {'photo'|'texture'|'model'|'thumb'|'export'} kind
 * @param {{width?:number, height?:number}} [meta]
 */
export async function upload(blob, kind, meta = {}) {
  const client = requireClient();
  const user = currentUser();
  if (!user) throw new Error('Sign in before uploading.');

  const sum = await checksum(blob);
  const { data: existing } = await client
    .from('assets').select('*')
    .eq('owner_id', user.id).eq('checksum', sum).eq('kind', kind)
    .limit(1).maybeSingle();
  if (existing) return existing;

  const id = crypto.randomUUID();
  const path = `${user.id}/${id}`;

  const { error: upErr } = await client.storage.from(BUCKET)
    .upload(path, blob, { contentType: blob.type || 'application/octet-stream', upsert: false });
  if (upErr) throw upErr;

  const { data: row, error } = await client.from('assets').insert({
    id, owner_id: user.id, kind, storage_path: path,
    mime: blob.type || null, bytes: blob.size,
    width: meta.width ?? null, height: meta.height ?? null,
    checksum: sum
  }).select().single();

  if (error) {
    // Do not leave a blob nobody has a row for.
    await client.storage.from(BUCKET).remove([path]).catch(() => {});
    throw error;
  }
  return row;
}

/** Upload straight from a data URL, which is the shape doc v1 stores. */
export async function uploadDataUrl(dataUrl, kind, meta) {
  return upload(await (await fetch(dataUrl)).blob(), kind, meta);
}

/** Link assets to a project so deleting one does not orphan the other. */
export async function linkToProject(projectId, assetIds) {
  const ids = [...new Set(assetIds.filter(Boolean))];
  if (!ids.length) return;
  const client = requireClient();
  const { error } = await client.from('project_assets')
    .upsert(ids.map(asset_id => ({ project_id: projectId, asset_id })), { onConflict: 'project_id,asset_id' });
  if (error) throw error;
}

/** Same idea, for presets — one asset can back several presets (or a preset
 *  and a project at once), so this is a second link, not a move. */
export async function linkToPreset(presetId, assetIds) {
  const ids = [...new Set(assetIds.filter(Boolean))];
  if (!ids.length) return;
  const client = requireClient();
  const { error } = await client.from('preset_assets')
    .upsert(ids.map(asset_id => ({ preset_id: presetId, asset_id })), { onConflict: 'preset_id,asset_id' });
  if (error) throw error;
}

/** Bytes stored by the signed-in user — what a storage quota would read. */
export async function usage() {
  if (!isConfigured || !currentUser()) return { bytes: 0, count: 0 };
  const { data, error } = await supabase.from('assets')
    .select('bytes').eq('owner_id', currentUser().id);
  if (error) throw error;
  return { bytes: data.reduce((s, r) => s + Number(r.bytes), 0), count: data.length };
}
